import { useCallback, useEffect, useRef, useState } from "react";
import { StatusIcon } from "../ui/statusIcon.tsx";
import { Button } from "../ui/button.tsx";
import { AnnouncementModal } from "./announcementModal.tsx";
import { getMyAnnouncements, markAnnouncementRead, type Announcement } from "../../api/announcements.ts";
import { useToast } from "../../hooks/useToast.ts";
import styles from "./announcement.module.scss";

/**
 * The announcement at the top of Cockpit. Shows the newest announcement the signed-in user has not marked as read;
 * "Read more" opens the full text, and "Mark as read" there writes the read receipt (a SuperAdmin sees who has read
 * what in the Announcements tool). They are the existing global announcements, fetched once per load.
 * A failed fetch shows nothing: a banner is never worth a broken page.
 */
export function AnnouncementBanner() {
  const { toast } = useToast();
  const [items, setItems] = useState<Announcement[]>([]);
  const [open, setOpen] = useState(false);
  const [marking, setMarking] = useState(false);

  // StrictMode double-mount: one fetch (bootedRef, so no `cancelled` flag — see AGENTS.md).
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    getMyAnnouncements()
      .then((r) => { if (!r.bError && Array.isArray(r.oData)) setItems(r.oData); })
      .catch(() => { /* no banner */ });
  }, []);

  const current = items[0] ?? null;

  const handleMarkRead = useCallback(async () => {
    if (!current) return;
    setMarking(true);
    try {
      const r = await markAnnouncementRead(current.AnnouncementId);
      if (r.bError) {
        toast(r.sMessage || "Could not save that you have read it. Try again.", "error");
        return;
      }
      setItems((prev) => prev.filter((a) => a.AnnouncementId !== current.AnnouncementId));
      setOpen(false);
      toast("Marked as read", "success");
    } catch {
      toast("Could not save that you have read it. Try again.", "error");
    } finally {
      setMarking(false);
    }
  }, [current, toast]);

  if (!current) return null;

  return (
    <>
      <section className={styles.banner} aria-label={`News: ${current.Title}`}>
        <span className={styles.icon} aria-hidden="true">
          <StatusIcon kind="info" size={20} style={{ color: "var(--color-primary)" }} />
        </span>
        <div className={styles.text}>
          <span className={styles.eyebrow}>News</span>
          <strong className={styles.title}>{current.Title}</strong>
          <span className={styles.preview}>{current.Body}</span>
        </div>
        <div className={styles.actions}>
          {current.AttachmentName && (
            <span className={styles.attachment}><StatusIcon kind="file" size={14} /> PDF</span>
          )}
          {items.length > 1 && <span className={styles.count}>1 of {items.length}</span>}
          <Button variant="primary" size="sm" onClick={() => setOpen(true)}>Read more</Button>
        </div>
      </section>
      <AnnouncementModal
        announcement={open ? current : null}
        onClose={() => { if (!marking) setOpen(false); }}
        onMarkRead={handleMarkRead}
        marking={marking}
      />
    </>
  );
}
